import { TrackLabel } from '@bariendo/lib/constants';
import { isPartnerTrack } from '@bariendo/lib/pac-inquiry-list';
import { getStoredPaymentStatus } from '@bariendo/lib/payment-status';
import { readPatientTrack } from '@bariendo/lib/revenue-ledger';
import { createReference } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TimelineRefundEvent } from '@/components/Timeline/types';
import { mapRefundHistoryToTimelineItems, RefundHistoryEntry } from '@/services/Timeline/refundCards';
import { fetchBotRead } from '@/services/botCalls';

export interface RefundRequestsState {
  cards: TimelineRefundEvent[];
  /**
   * The requests themselves; undefined until read, and after a failed read. The refund row reads
   * them beside the stored payment status, so a cancellation whose restage failed (the status
   * still Won) can't be raised twice — and a read that failed is "unknown", never "none".
   */
  requests?: RefundHistoryEntry[];
  /** The first read for this patient has not settled, so no cards is not yet "none". */
  loading: boolean;
  /** Re-read after the refund or cancellation composer writes (BAR-3069). */
  refresh: () => Promise<void>;
}

/**
 * The patient's refund request cards (BAR-3052), through the payments bot, with a refresh for
 * the composer that writes them (BAR-3069).
 * `undefined` reads nothing, which is how the page gates it on payments access.
 * A failed read shows no cards rather than an error: they decorate a timeline.
 */
export function useRefundRequests(patient: Patient | undefined): RefundRequestsState {
  // Finance's approval and linking move the stored payment status, which reaches the page live: a
  // change re-reads, so Refund due and the cards follow without a reload.
  const statusKey = patient ? paymentStatusKey(patient) : undefined;
  const medplum = useMedplum();
  const [state, setState] = useState<{ patientId: string; requests?: RefundHistoryEntry[] }>();
  const patientId = patient?.id;
  const patientRef = useRef(patient);
  patientRef.current = patient;
  const currentPatientId = useRef(patientId);
  currentPatientId.current = patientId;

  const refresh = useCallback(async (): Promise<void> => {
    const target = patientRef.current;
    if (!target?.id || target.id !== patientId) return;
    let requests: RefundHistoryEntry[] | undefined;
    try {
      ({ requests } = await fetchBotRead<{ requests: RefundHistoryEntry[] }>(medplum, 'create-treatment-payment', {
        operation: 'refundHistory',
        patients: [createReference(target)],
      }));
    } catch {
      // No cards rather than an error.
    }
    // A read for a patient no longer on screen never lands.
    if (currentPatientId.current === patientId) {
      setState({ patientId, requests });
    }
  }, [medplum, patientId]);

  useEffect(() => {
    void refresh();
  }, [refresh, statusKey]);

  const track = patient ? readPatientTrack(patient) : undefined;
  // The stored status names the payor on every coverage track, the insurer included (BAR-3082).
  const payor =
    (patient && getStoredPaymentStatus(patient)?.payor) ??
    (track && isPartnerTrack(track) ? TrackLabel[track] : undefined);
  const requests = state && state.patientId === patientId ? state.requests : undefined;

  const cards = useMemo(() => (requests ? mapRefundHistoryToTimelineItems(requests, payor) : []), [requests, payor]);
  return { cards, requests, loading: !!patientId && state?.patientId !== patientId, refresh };
}

function paymentStatusKey(patient: Patient): string | undefined {
  const stored = getStoredPaymentStatus(patient);
  return stored ? `${stored.status}|${stored.at}` : undefined;
}
